import { ECardinality } from "@kuika/kuika-cl-model";
import { CascaderOptionType } from "antd/es/cascader";
import { AttachableActionOption } from "./attachable-actions";
import { extractLeafOptions, ManifestBinding } from "./manifest-extractor";
import { encodeSymbolRef } from "./symbol-ref";

/**
 * A single property slot in the bindables manifest — either a value slot
 * (anything pickable via the cascader) or an event slot (attachable action
 * definitions). The LLM consumes `options` directly and hands back a ref in
 * `ui_bind`.
 */
export interface BindablePropertySlot {
  kind: "value" | "event";
  targetType?: string;
  targetCardinality?: ECardinality;
  options: ManifestBinding[];
  /** Present only when `maxOptionsPerCategory` cut this slot: category → options left out. */
  omittedByCategory?: Record<string, number>;
}

export interface BindablesManifestEntry {
  kuikaId: string;
  componentName: string;
  properties: Record<string, BindablePropertySlot>;
}

export type BindablesManifest = Record<string, BindablesManifestEntry>;

export const actionDefsToManifestBindings = (defs: AttachableActionOption[]): ManifestBinding[] =>
  defs.map((def) => ({
    ref: encodeSymbolRef({ kind: "actionDef", actionDefinitionId: def.id }),
    label: def.displayName ?? def.name,
    category: "actionDef",
    type: def.returnTypeName,
    cardinality: def.returnCardinality,
    parameters: def.parameters
  }));

export interface ComposePropertySlotInput {
  kind: "value" | "event";
  targetType?: string;
  targetCardinality?: ECardinality;
  cascaderOptions?: CascaderOptionType[];
  actionDefs?: AttachableActionOption[];
}

export const composePropertySlot = (input: ComposePropertySlotInput): BindablePropertySlot => {
  if (input.kind === "event") {
    return {
      kind: "event",
      targetType: input.targetType,
      targetCardinality: input.targetCardinality,
      options: actionDefsToManifestBindings(input.actionDefs ?? [])
    };
  }
  return {
    kind: "value",
    targetType: input.targetType,
    targetCardinality: input.targetCardinality,
    options: extractLeafOptions(input.cascaderOptions ?? [])
  };
};

/**
 * Every `ManifestBinding.category` the manifest can emit — the vocabulary the
 * `categories` narrowing filter accepts. `actionDef` is the event-slot category.
 */
export const MANIFEST_BINDING_CATEGORIES: readonly string[] = [
  "default",
  "screenInput",
  "displayItemProp",
  "actionResult",
  "actionField",
  "current",
  "bag",
  "actionDef"
];

/** Optional output narrowing for the bindables manifest. Both absent = the full manifest. */
export interface SlotNarrowing {
  /** Keep only options of these categories (matched case-insensitively). */
  categories?: string[];
  /** Keep at most this many options per category, per slot. */
  maxOptionsPerCategory?: number;
}

export const isSlotNarrowingActive = (narrowing: SlotNarrowing): boolean =>
  (narrowing.categories !== undefined && narrowing.categories.length > 0) ||
  narrowing.maxOptionsPerCategory !== undefined;

/**
 * Apply {@link SlotNarrowing} to one slot. Pure: returns the SAME slot object
 * when nothing is narrowed, so the un-narrowed manifest is byte-identical to
 * what it was before narrowing existed.
 */
export const narrowPropertySlot = (slot: BindablePropertySlot, narrowing: SlotNarrowing): BindablePropertySlot => {
  if (!isSlotNarrowingActive(narrowing)) return slot;

  const wanted: Set<string> = new Set<string>(
    (narrowing.categories ?? []).map((category: string): string => category.toLowerCase())
  );
  const limit: number | undefined = narrowing.maxOptionsPerCategory;
  const keptPerCategory: Map<string, number> = new Map<string, number>();
  const omittedByCategory: Record<string, number> = {};
  const options: ManifestBinding[] = [];

  for (const option of slot.options) {
    if (wanted.size > 0 && !wanted.has(option.category.toLowerCase())) continue;
    const kept: number = keptPerCategory.get(option.category) ?? 0;
    if (limit !== undefined && kept >= limit) {
      omittedByCategory[option.category] = (omittedByCategory[option.category] ?? 0) + 1;
      continue;
    }
    keptPerCategory.set(option.category, kept + 1);
    options.push(option);
  }

  const narrowed: BindablePropertySlot = { ...slot, options };
  if (Object.keys(omittedByCategory).length > 0) narrowed.omittedByCategory = omittedByCategory;
  return narrowed;
};
